import axios from 'axios';
import { ThemeMode } from '@drivably-apps/component-lib';

import {
  countryValues,
  layoutValues,
  modalPositionValues,
  RegexHexColor,
} from '@app/constants';
import {
  AttrEnableEmail,
  DisplayNameDefault,
  DivID,
  OfferDealerTextLimit,
  ScriptSrcProd,
  SingleApiUrl,
} from '@app/data';
import { Layout, ModalPosition, Mode, OfferSMS } from '@app/enums';
import { DomAttributes, StoreInformation } from '@app/interfaces';
import { WidgetThemeSetProperty, WidgetThemeShades } from '@app/types';
import { locale, loggerCl, errors } from '@app/lib';

import { logger } from './logger.utils';

const modeValues = Object.values(Mode);
const offerSMSValues = Object.values(OfferSMS);

// Default time to wait until missing node error is logged
const ContainerDetectionDelay = 10000;
// Refetch attributes after this interval
const WidgetAttributesValidityMinutes = 15;
const WidgetAttributesStorageKey = 'clcdpe-params';

/** Gain access to the element container */
export const getContainerNode = (container: string): HTMLElement =>
  document.querySelector(`#${container}`) as HTMLElement;

/** Set property to a given HTMLElement */
export const setNodeProperty = <T extends WidgetThemeSetProperty>(
  node: T['node'],
  propName: T['propName'],
  propValue: T['propValue']
): void => node.style.setProperty(propName, propValue);

/** Set main, light and dark shades for each color type */
export const generateShades = (color: string): WidgetThemeShades => ({
  main: color,
  light: adjustHexColor(color, 30),
  dark: adjustHexColor(color, -40),
});

/** Lighten or darken a color */
export const adjustHexColor = (hex = '', offset = 0): string => {
  let hasPound = false;
  if (hex[0] === '#') {
    hex = hex.slice(1);
    hasPound = true;
  }

  const num = parseInt(hex, 16);
  let r = (num >> 16) + offset;
  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let b = ((num >> 8) & 0x00ff) + offset;
  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  let g = (num & 0x0000ff) + offset;
  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  return (hasPound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
};

/** Detect if a color is bright/dark so we could decide to apply dark mode or not */
export const isHexColorLight = (hex: string): boolean => {
  const newHex = +`0x${hex
    .slice(1)
    .replace((hex.length < 5).toString() && /./g, '$&$&')}`;

  const r = newHex >> 16;
  const g = (newHex >> 8) & 255;
  const b = newHex & 255;

  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return hsp > 160;
};

/**
 * Attempt to reinitialize the application when duplicate scripts are loaded in
 * single page. This is a workaround for dealer setup issues.
 */
export const reloadWidgetApp = (): boolean => {
  const duplicatedScripts = document.querySelectorAll(
    `[src="${ScriptSrcProd}"]`
  );
  const hasDuplicates = duplicatedScripts.length >= 2;
  if (hasDuplicates) {
    setTimeout(() => {
      const widgetScript = document.createElement(
        'script'
      ) as HTMLScriptElement;
      widgetScript.src = ScriptSrcProd;
      widgetScript.defer = true;
      widgetScript.async = true;
      document.body.append(widgetScript);
    }, 100);

    duplicatedScripts.forEach((script) => script.remove());
  }

  return hasDuplicates;
};

/** Parse HTML attributes on root container and set the store configuration */
export const parseAttributes = (
  widgetParams: Partial<DomAttributes> = {}
): DomAttributes => {
  const shouldForceConsole = { forceConsole: true };
  const updatedParams = { ...widgetParams } as DomAttributes;

  if (!updatedParams.storeId) {
    const message = locale.formatLocale(locale.appErrorSetupNoStoreId, DivID);
    updatedParams.hasInitError = true;

    logger.error(message, shouldForceConsole);
  }

  if (/[^0-9]/.test(updatedParams.storeId)) {
    const message = updatedParams.storeId
      ? locale.formatLocale(
          locale.appErrorSetupStoreIdFormat,
          updatedParams.storeId
        )
      : locale.formatLocale(locale.appErrorSetupEmptyStoreId, DivID);

    updatedParams.hasInitError = true;

    logger.error(message, shouldForceConsole);
  }

  if (!updatedParams.country) {
    const message = locale.formatLocale(locale.appErrorSetupNoCountry, DivID);
    updatedParams.hasInitError = true;

    logger.error(message, shouldForceConsole);
  }

  if (
    !!updatedParams.country &&
    !countryValues.includes(updatedParams.country)
  ) {
    const values = generateStringList(countryValues);
    const message = locale.appErrorSetupCountryValues;
    const words = [updatedParams.country, DivID, values];
    const messageUpdated = locale.formatLocale(message, words);
    updatedParams.hasInitError = true;

    logger.error(messageUpdated, shouldForceConsole);
  }

  // Hex attributes
  updatedParams.primaryColor = setColorAttr(
    updatedParams.primaryColor,
    '',
    'data-primary-color'
  );

  updatedParams.theme = updatedParams.theme || ThemeMode.light;

  updatedParams.accentColor = setColorAttr(
    updatedParams.accentColor,
    updatedParams.primaryColor,
    'data-accent-color'
  );

  // Set external logo color depending on store
  updatedParams.logoColor = setColorAttr(
    updatedParams.logoColor,
    '',
    'data-logo-color'
  );

  updatedParams.offerDealerText = setTextAttrLimit(
    updatedParams.offerDealerText,
    '',
    OfferDealerTextLimit,
    'data-offer-dealer-text'
  );

  updatedParams.modalPosition = setEnumAttr(
    updatedParams.modalPosition,
    ModalPosition.Center,
    modalPositionValues,
    'data-modal-position'
  );

  updatedParams.layout = setEnumAttr(
    updatedParams.layout,
    Layout.Responsive,
    layoutValues,
    'data-layout'
  );

  updatedParams.sendOfferSms = setEnumAttr(
    updatedParams.sendOfferSms,
    OfferSMS.None,
    offerSMSValues,
    'data-sent-offer-sms'
  );

  // Boolean attributes
  updatedParams.hasAccident = setBoolAttr(updatedParams.accident);
  updatedParams.hasAskingPrice = setBoolAttr(updatedParams.askingPrice);
  updatedParams.hasNoReferralCode = setBoolAttr(
    updatedParams.noReferralCode,
    true
  );
  updatedParams.hasNotes = setBoolAttr(updatedParams.notes);
  updatedParams.hasOfferStep = setBoolAttr(updatedParams.offerStep, true);
  updatedParams.hasOwnershipScreen = setBoolAttr(
    updatedParams.ownershipScreen,
    true
  );
  updatedParams.hasVehicleCondition = setBoolAttr(
    updatedParams.vehicleCondition
  );
  updatedParams.isBodyTextCentered = setBoolAttr(updatedParams.textCentered);
  updatedParams.isInspectionOffer = setBoolAttr(updatedParams.inspectionOffer);
  updatedParams.shouldShowOnMount = setBoolAttr(updatedParams.showOnMount);
  updatedParams.shouldConfirmOffer = setBoolAttr(updatedParams.offerConfirm);
  updatedParams.isCentered = setBoolAttr(updatedParams.centered);

  updatedParams.hasTradeIn = setBoolAttr(updatedParams.tradeIn);
  updatedParams.hasWindshieldCrackQuestion = setBoolAttr(
    updatedParams.windshield
  );

  updatedParams.isEmbeddedFull = setBoolAttr(updatedParams.embeddedFull);
  updatedParams.isEmbedded =
    updatedParams.isEmbeddedFull || setBoolAttr(updatedParams.embedded);

  updatedParams.layout = updatedParams.isEmbeddedFull
    ? Layout.Mobile
    : updatedParams.layout;

  updatedParams.embedded = setBoolAttr(updatedParams.embedded);
  updatedParams.offerStep = setBoolAttr(updatedParams.offerStep);
  updatedParams.inspectionOffer = setBoolAttr(updatedParams.inspectionOffer);

  updatedParams.hasEmail = AttrEnableEmail || setBoolAttr(updatedParams.email);

  updatedParams.phoneNotRequired = updatedParams.isInspectionOffer
    ? false
    : setBoolAttr(updatedParams.phoneNotRequired);
  updatedParams.scanner = setBoolAttr(updatedParams.scanner, true);
  updatedParams.customerOptIn = setBoolAttr(updatedParams.customerOptIn, false);
  updatedParams.emailRequired = setBoolAttr(updatedParams.emailRequired, false);
  updatedParams.termsTextItalic = setBoolAttr(
    updatedParams.termsTextItalic,
    false
  );

  updatedParams.photoCaptureSms = setBoolAttr(
    updatedParams.photoCaptureSms,
    false
  );

  // Number attributes
  updatedParams.maxOffer = setNumberAttr(updatedParams.maxOffer, Infinity);

  // Text attributes
  updatedParams.storeDisplayName = DisplayNameDefault;

  updatedParams.introHeaderText = setTextAttr(
    updatedParams.introHeaderText,
    locale.appGetOfferHeadline
  );
  if (updatedParams.introHeaderText) {
    updatedParams.introHeaderText = updatedParams.introHeaderText.trim();
  }

  updatedParams.introDescriptionText = setTextAttr(
    updatedParams.introDescriptionText,
    locale.appGetOfferDescription
  );
  if (updatedParams.introDescriptionText) {
    updatedParams.introDescriptionText =
      updatedParams.introDescriptionText.trim();
  }

  updatedParams.contactInfoText = setTextAttr(
    updatedParams.contactInfoText,
    updatedParams.phoneNotRequired
      ? locale.appContactInfoDescriptionNoPhone
      : locale.appContactInfoDescription
  );

  updatedParams.logoImage = setTextAttr(updatedParams.logoImage, '');
  updatedParams.termsText = setTextAttr(updatedParams.termsText, '');

  // Customize vehicle selection tab
  updatedParams.defaultVehicleMode = setEnumAttr(
    updatedParams.defaultVehicleMode,
    '',
    modeValues,
    'data-default-vehicle-mode'
  );

  updatedParams.hasMakeModel = setBoolAttr(updatedParams.makeModel, true);
  updatedParams.hasVin = setBoolAttr(updatedParams.vin, true);
  updatedParams.hasLicensePlate = setBoolAttr(updatedParams.licensePlate, true);

  if (
    !updatedParams.hasMakeModel &&
    !updatedParams.hasVin &&
    !updatedParams.hasLicensePlate
  ) {
    logger.error(
      'All vehicle selection modes (License Plate, VIN and MakeModel) have been disabled. At least one must be enabled.',
      shouldForceConsole
    );

    updatedParams.hasMakeModel = true;
    updatedParams.hasVin = true;
    updatedParams.hasLicensePlate = true;
  }

  return updatedParams;
};

const generateStringList = (values: string[] = []) =>
  values.map((value) => `"${value}"`).join(', ');

const setTextAttr = <T>(
  value: string | string[],
  defaultValue: string | string[]
) => (typeof value === 'undefined' ? defaultValue : value) as T;

const setBoolAttr = (value?: boolean, defaultValue = false) =>
  typeof value === 'undefined' ? defaultValue : value.toString() === 'true';

const setNumberAttr = (value?: string | number, defaultValue = 0) =>
  typeof value === 'undefined' ? defaultValue : Number(value);

const setEnumAttr = <T>(
  value: string,
  defaultValue: string,
  list: string[],
  name: string
) => {
  if (!value) {
    return defaultValue;
  }

  if (list.includes(value)) {
    return value;
  }

  const values = generateStringList(list);

  logger.error(
    locale.formatLocale(locale.appErrorSetupEnumAttributes, [
      value,
      DivID,
      name,
      values,
    ]),
    { forceConsole: true }
  );

  return defaultValue as T;
};

const setTextAttrLimit = (
  value: string,
  defaultValue: string,
  maxLength: number,
  name: string
) => {
  if (!value) {
    return defaultValue;
  }
  if (value?.trim()?.length > maxLength) {
    logger.error(
      locale.formatLocale(locale.appErrorSetupTextAttributesLimit, [
        name,
        `${maxLength}`,
      ]),
      { forceConsole: true }
    );
    return defaultValue;
  }

  return value;
};

const setColorAttr = (value: string, defaultValue: string, name: string) => {
  if (value) {
    if (!RegexHexColor.test(value)) {
      logger.error(
        locale.formatLocale(locale.appErrorSetupColorAttributes, [
          value,
          DivID,
          name,
        ]),
        { forceConsole: true }
      );
      return defaultValue;
    }

    return value;
  }

  return defaultValue;
};

/** This method pretends to add the necessary classes to initialize the root div */
export const addInitializerClasses = (
  root: HTMLElement,
  widgetParams: DomAttributes
) => {
  if (widgetParams.gutter) {
    root.classList.add(`gutter-${widgetParams.gutter}`);
  }
};

export const listenDOM = ({
  delay = ContainerDetectionDelay,
  elementId,
  onAdd,
  onError,
  onRemove,
}: {
  delay?: number;
  elementId: string;
  onAdd(node: HTMLElement): void;
  onError(): void;
  onRemove(node: HTMLElement): void;
}) => {
  let timeoutId = 0;

  const staticNode = getContainerNode(elementId);
  if (staticNode) {
    onAdd(staticNode);
  } else {
    const timeoutRes = setTimeout(() => {
      // Log error if a container has not been detected for a certain amount of time
      onError();
    }, delay);

    timeoutId = timeoutRes as unknown as number;
  }

  // Detect DOM changes
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        for (const node of mutation.removedNodes) {
          const containerNode = node as HTMLElement;

          const id = containerNode.getAttribute?.('id');
          if (id === elementId) {
            onRemove(containerNode);
          }
        }

        for (const node of mutation.addedNodes) {
          const containerNode = node as HTMLElement;

          const id = containerNode.getAttribute?.('id');
          if (id === elementId) {
            // Cancel error log once node is detected
            clearTimeout(timeoutId);

            onAdd(containerNode);
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

const getSessionStorage = (key: string) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    loggerCl.error(errors.sessionStorageGetFailed, {
      data: { key },
      error: (error as Error).message,
    });

    return null;
  }
};

const setSessionStorage = (
  key: string,
  data: object | string | number | boolean
) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    loggerCl.error(errors.sessionStorageSaveFailed, {
      data: { key, data },
    });
  }
};

interface WidgetAttributesResponseData {
  id: string;
  data_store_id: string | null;
  data_country: string;
  data_accent_color: string | null;
  data_accident: string | null;
  data_appraisal: string | null;
  data_asking_price: string | null;
  data_centered: string | null;
  data_contact_info_text: string | null;
  data_customer_opt_in: string | null;
  data_default_vehicle_mode: string | null;
  data_email_required: string | null;
  data_email: string | null;
  data_embedded_full: string | null;
  data_embedded: string | null;
  data_gutter: string | null;
  data_height: string | null;
  data_inspection_offer: string | null;
  data_intro_description_text: string | null;
  data_intro_header_text: string | null;
  data_layout: string | null;
  data_license_plate: string | null;
  data_logo_color: string | null;
  data_logo_image: string | null;
  data_make_model: string | null;
  data_max_offer: string | null;
  data_modal_position: string | null;
  data_no_referral_code: string | null;
  data_notes: string | null;
  data_offer_confirm: string | null;
  data_offer_dealer_text: string | null;
  data_offer_step: string | null;
  data_ownership_screen: string | null;
  data_phone_not_required: string | null;
  data_photo_capture_sms: string | null;
  data_primary_color: string | null;
  data_scanner: string | null;
  data_send_offer_email: string | null;
  data_send_offer_sms: string | null;
  data_show_on_mount: string | null;
  data_terms_text_italic: string | null;
  data_terms_text: string | null;
  data_text_centered: string | null;
  data_trade_in: string | null;
  data_vehicle_condition: string | null;
  data_vin: string | null;
  data_width: string | null;
  data_windshield: string | null;
  ddc_banner_image_url: string;
  ddc_cta_text: string;
  ddc_show_cta_on_srp_pages: string;
  dealer_account_id: string;
  dealer_site_id: string;
  store_id: string;
}

/**
 * Obtain widget store and attributes (html attributes with store configuration) from
 * server or session storage cache
 **/
export const fetchWidgetStoreAndAttributes = async ({
  storeId,
}: {
  storeId?: string;
}): Promise<
  | { store?: StoreInformation; attributes?: WidgetAttributesResponseData }
  | undefined
> => {
  if (!storeId) {
    return;
  }

  // Get saved attributes from storage
  const storage = getSessionStorage(WidgetAttributesStorageKey);

  // Use saved store and attributes for current store if applicable. Otherwise, fetch from server.
  if (
    storage &&
    storage?.[storeId]?.updated_date &&
    (storage?.[storeId]?.attributes || storage?.[storeId]?.store)
  ) {
    // Check last saved time if valid
    const storageDate = new Date(storage[storeId].updated_date);
    const currentDate = new Date();

    const minutesElapsed = Math.floor(
      Math.abs(currentDate.getTime() - storageDate.getTime()) / 1000 / 60
    );

    // Return saved attributes if data is still "fresh"
    if (minutesElapsed < WidgetAttributesValidityMinutes) {
      return {
        attributes: storage[storeId].attributes,
        store: storage[storeId].store,
      };
    }
  }

  try {
    const result = await axios.get(`${SingleApiUrl}/WidgetParams/${storeId}`);

    const attributes = result.data?.widget_params?.[0];
    const store = result.data?.store;

    if (attributes || store) {
      // Save results to storage
      setSessionStorage(WidgetAttributesStorageKey, {
        ...storage,
        [storeId]: {
          attributes,
          store,
          updated_date: new Date(),
        },
      });
    }

    return {
      attributes,
      store,
    };
  } catch (error) {
    loggerCl.error(errors.attributesFetchFailed, {
      error: (error as Error).message,
    });

    return;
  }
};

/** Set fetched attributes to DOM. Merge it with currently existing ones defined by dealer. */
export const attachAttributesToDOM = (
  root: HTMLElement,
  attributes?: WidgetAttributesResponseData
) => {
  if (!attributes) {
    return;
  }

  for (const key in attributes) {
    const value = (attributes as unknown as Record<string, string>)[key];

    // Attach properties with value set
    if (key.startsWith('data_') && Boolean(value)) {
      const attributeName = key.replaceAll('_', '-');
      root.setAttribute(attributeName, `${value}`);
    }
  }
};
